import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { adminAuthApi } from '@/api/adminAuth'
import type { AdminInfo } from '@/api/adminTypes'
import { ADMIN_AUTH_TOKEN_KEY } from '@/api/client'

type AdminAuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

interface AdminAuthContextValue {
  token: string | null
  admin: AdminInfo | null
  status: AdminAuthStatus
  login(token: string, admin: AdminInfo): void
  logout(): Promise<void>
  clearSession(): void
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null)

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(ADMIN_AUTH_TOKEN_KEY))
  const [admin, setAdmin] = useState<AdminInfo | null>(null)
  const [status, setStatus] = useState<AdminAuthStatus>(() =>
    localStorage.getItem(ADMIN_AUTH_TOKEN_KEY) ? 'loading' : 'unauthenticated',
  )

  useEffect(() => {
    const saved = localStorage.getItem(ADMIN_AUTH_TOKEN_KEY)
    if (!saved) {
      setStatus('unauthenticated')
      return
    }

    let cancelled = false
    adminAuthApi.me()
      .then(currentAdmin => {
        if (cancelled) return
        setToken(saved)
        setAdmin(currentAdmin)
        setStatus('authenticated')
      })
      .catch(() => {
        if (cancelled) return
        localStorage.removeItem(ADMIN_AUTH_TOKEN_KEY)
        setToken(null)
        setAdmin(null)
        setStatus('unauthenticated')
      })

    return () => { cancelled = true }
  }, [])

  const value = useMemo<AdminAuthContextValue>(() => {
    const clearSession = () => {
      localStorage.removeItem(ADMIN_AUTH_TOKEN_KEY)
      setToken(null)
      setAdmin(null)
      setStatus('unauthenticated')
    }

    return {
      token,
      admin,
      status,
      login(newToken, currentAdmin) {
        localStorage.setItem(ADMIN_AUTH_TOKEN_KEY, newToken)
        setToken(newToken)
        setAdmin(currentAdmin)
        setStatus('authenticated')
      },
      async logout() {
        try {
          await adminAuthApi.logout()
        } catch {
          // 本地会话必须清理，避免 token 已失效时卡住后台页面。
        }
        clearSession()
      },
      clearSession,
    }
  }, [token, admin, status])

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>
}

export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext)
  if (!ctx) throw new Error('useAdminAuth must be used within <AdminAuthProvider>')
  return ctx
}
